import {
  moveGroup,
  nextGridLine,
  ResizeEdge,
  resizeBlockNoFlip,
  type CreateOptions,
  type IdSpan,
} from '@wiztime/core';
import { PickerFlushReason } from '@wiztime/core';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isEditableTarget } from '../../shell/shortcuts';
import { BlockKeyType, MoveStep, resolveBlockKey } from './blockKeys';
import type { DayData } from './DayDataContext';
import { focusGroup, groupAnchor } from './blockDom';
import { ContextMenuSource, PickerFocus, PickerKind, useDayUi, type SelectedGroupInfo } from './dayUiStore';
import { flushPicker } from './picker/pickerFlush';
import { topLevelSelectable } from './projectGroups';
import { QuickMarkMode, QuickMarkSource } from './quickMark';
import { markBesideSelection } from './tileMark';
import type { DayCommands } from './useDayCommands';

function isDayDataList(data: DayData | readonly DayData[]): data is readonly DayData[] {
  return Array.isArray(data);
}

const HOUR_MIN = 60;

/**
 * Keys that act on the selected block — or, for whole-block actions, on the focused block when
 * nothing is selected (ui-redesign §3.2.1): Del clears, ArrowUp/ArrowDown resize an edge,
 * Alt+arrows and Alt+PageUp/PageDown move the whole block or group, Ctrl/Cmd+Enter overlays,
 * Shift+Enter opens the overlap popover (only when the block has one), ArrowLeft/ArrowRight
 * move focus alone between overlap columns while focus is on an overlapping block, `?` opens
 * the gesture help. While the picker or overlap popover is open, Alt moves, Del and
 * Ctrl/Cmd+Enter still reach the selected block; bare keys belong to the popover (§3.5).
 * Keys that end up acting on nothing are not consumed: they stay with the page.
 * Esc follows the priority order of §3.2.1 (a drag cancel is consumed upstream by the lane).
 * The week view passes the data of all its days; the target block is looked up among them.
 */
export function useDayKeyboard(data: DayData | readonly DayData[], commands: DayCommands): void {
  const { t } = useTranslation();
  const latest = useRef({ data, commands, t });
  useEffect(() => {
    latest.current = { data, commands, t };
  });

  // Publish the selected group for the quick-mark tiles, which live outside the day data
  // context (design 2026-10-15 §1.2): they add a parallel block beside it.
  const selectedKey = useDayUi((state) => state.selectedKey);
  const selectedExplicit = useDayUi((state) => state.selectedExplicit);
  useEffect(() => {
    const days: readonly DayData[] = isDayDataList(data) ? data : [data];
    let info: SelectedGroupInfo | null = null;
    for (const day of days) {
      const group = selectedKey === null ? undefined : day.groups.find((item) => item.key === selectedKey);
      if (group !== undefined) {
        info = {
          key: group.key,
          date: day.date,
          span: { startMin: group.startMin, endMin: group.endMin },
          projectId: group.projectId,
          explicit: selectedExplicit,
        };
        break;
      }
    }
    useDayUi.getState().setSelectedGroup(info);
  }, [data, selectedKey, selectedExplicit]);
  // Leaving the view leaves no published selection behind for the tiles of the next one.
  useEffect(() => () => useDayUi.getState().setSelectedGroup(null), []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.defaultPrevented || isEditableTarget(event.target)) return;
      const ui = useDayUi.getState();
      // The open context menu owns the keyboard (it handles its own keys, 2026-10b §6.3).
      if (ui.contextMenu !== null) return;

      if ((event.key === '?' || event.key === '？') && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        ui.toggleHelp();
        return;
      }

      if (event.key === 'Escape') {
        // §3.2.1 as updated by 2026-10b §4.3: cancel drag (lane hook, upstream) > close
        // popover keeping the selection (Popover/Sheet handle their own Esc) > exit quick
        // mark (Once or Locked, one Esc even while a block is selected) > deselect.
        if (ui.helpOpen) {
          event.preventDefault();
          ui.closeHelp();
          return;
        }
        if (ui.picker !== null || ui.conflict !== null) return;
        if (ui.quickMark.mode !== QuickMarkMode.Off) {
          event.preventDefault();
          ui.quickMarkExit();
          return;
        }
        if (ui.selectedKey !== null) ui.select(null);
        return;
      }

      // Digit keys 1–9 pick the n-th quick-mark tile (settings order, 2026-10b §4.8); Shift
      // locks. By KeyboardEvent.code, numpad included; never with Ctrl/Alt/Meta, never while
      // a popover, the help overlay or a drag is open — an open picker consumes digits itself.
      const digit = /^(?:Digit|Numpad)([1-9])$/.exec(event.code);
      if (digit !== null && !event.ctrlKey && !event.metaKey && !event.altKey) {
        // A held digit key must not repeat the mark (or toggle quick mark on and off).
        if (event.repeat) return;
        if (ui.picker !== null || ui.conflict !== null || ui.helpOpen || ui.dragSpan !== null) return;
        const { data: all } = latest.current;
        const first = isDayDataList(all) ? all[0] : all;
        const tiles = first === undefined ? [] : topLevelSelectable(first.projects.all, first.projects.byId);
        const project = tiles[Number(digit[1]) - 1];
        if (project === undefined) return;
        event.preventDefault();
        if (event.shiftKey) ui.quickMarkLock(project.id);
        // With a block group selected the digit adds a parallel block beside it instead of
        // arming quick mark (design 2026-10-15 §1.2).
        else if (!markBesideSelection(project.id, latest.current.commands.createBlock, latest.current.t)) {
          ui.quickMarkActivate(project.id, QuickMarkSource.Digit);
        }
        return;
      }

      // Menu key or Shift+F10 opens the block context menu on the focused block, or else the
      // selected one (2026-10b §6.3): anchored 12px inside the block's top-left corner, or in
      // the middle of the viewport when the block is scrolled out of view.
      const menuKey =
        event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey && !event.ctrlKey);
      if (menuKey && !event.metaKey && !event.altKey) {
        const { data: all } = latest.current;
        const allDays: readonly DayData[] = isDayDataList(all) ? all : [all];
        const focusedKey =
          document.activeElement instanceof HTMLElement
            ? (document.activeElement.dataset['groupKey'] ?? null)
            : null;
        const key = focusedKey ?? ui.selectedKey;
        if (key === null) return;
        const day = allDays.find((candidate) => candidate.groups.some((item) => item.key === key));
        const anchor = groupAnchor(key);
        if (day === undefined || anchor === null) return;
        event.preventDefault();
        const visible =
          anchor.bottom > 0 &&
          anchor.top < window.innerHeight &&
          anchor.right > 0 &&
          anchor.left < window.innerWidth;
        ui.openContextMenu({
          date: day.date,
          groupKey: key,
          x: visible ? anchor.left + 12 : window.innerWidth / 2,
          y: visible ? anchor.top + 12 : window.innerHeight / 2,
          source: ContextMenuSource.Keyboard,
        });
        return;
      }

      // F2 opens the picker focused on the title field (2026-10b §5.3) for the selected
      // block, or the focused one.
      if (event.key === 'F2' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const { data: all } = latest.current;
        const allDays: readonly DayData[] = isDayDataList(all) ? all : [all];
        const focusedKey =
          document.activeElement instanceof HTMLElement
            ? (document.activeElement.dataset['groupKey'] ?? null)
            : null;
        const key = ui.selectedKey ?? focusedKey;
        if (key === null) return;
        const day = allDays.find((candidate) => candidate.groups.some((item) => item.key === key));
        const target = day?.groups.find((candidate) => candidate.key === key);
        const anchor = groupAnchor(key);
        if (day === undefined || target === undefined || anchor === null) return;
        event.preventDefault();
        ui.openPicker({
          kind: PickerKind.Edit,
          date: day.date,
          groupKey: target.key,
          anchor,
          focus: PickerFocus.Title,
        });
        return;
      }

      const action = resolveBlockKey(event);
      if (action === null) return;
      const overlayOpen = ui.picker !== null || ui.conflict !== null;
      // Clear through an open popover only on Del (§3.5): Backspace on a focused picker option
      // must not delete the block it is about.
      const actsThroughOverlay =
        (action.type === BlockKeyType.Clear && event.key === 'Delete') ||
        action.type === BlockKeyType.MoveWhole ||
        action.type === BlockKeyType.Overlay;
      if (overlayOpen && !actsThroughOverlay) return;

      const { data: all, commands: api } = latest.current;
      const days: readonly DayData[] = isDayDataList(all) ? all : [all];
      // Target: the selected group, else (whole-block actions only) the focused block (§3.2.1).
      const focused =
        document.activeElement instanceof HTMLElement
          ? (document.activeElement.dataset['groupKey'] ?? null)
          : null;

      if (action.type === BlockKeyType.ColumnMove) {
        // §3.5: ←/→ move focus between the side-by-side overlap columns, and only while focus
        // is on a block that sits in an overlap layout with a neighbour in the target column.
        // In every other case the keys stay with the page (week-view horizontal scrolling,
        // other controls), and the selection is never changed — focus moves alone.
        if (focused === null) return;
        const day = days.find((candidate) => candidate.groups.some((item) => item.key === focused));
        const origin = day?.groups.find((candidate) => candidate.key === focused);
        const layout = day?.conflicts.layout.get(focused);
        if (day === undefined || origin === undefined || layout === undefined) return;
        const targetColumn = layout.column + action.direction;
        const sibling = day.groups.find((candidate) => {
          if (candidate.key === origin.key) return false;
          const candidateLayout = day.conflicts.layout.get(candidate.key);
          return (
            candidateLayout !== undefined &&
            candidateLayout.column === targetColumn &&
            candidate.startMin < origin.endMin &&
            candidate.endMin > origin.startMin
          );
        });
        if (sibling === undefined) return;
        event.preventDefault();
        focusGroup(sibling.key);
        return;
      }

      const nudging = action.type === BlockKeyType.Nudge;
      const targetKey = ui.selectedKey ?? (nudging ? null : focused);
      if (targetKey === null) return;
      const current = days.find((day) => day.groups.some((candidate) => candidate.key === targetKey));
      const group = current?.groups.find((candidate) => candidate.key === targetKey);
      if (current === undefined || group === undefined) return;
      const options: CreateOptions = {
        step: current.settings.timeStepMin,
        minDuration: current.settings.minDurationMin,
        dayStartMin: current.settings.dayStartMin,
        defaultDurationMin: current.settings.defaultDurationMin,
      };

      if (action.type === BlockKeyType.OverlapPanel) {
        // Shift+Enter is consumed only when the block actually overlaps another confirmed
        // block (§3.5 "块有重叠"); otherwise the key is left to the page.
        const overlapping = current.conflicts.pairs.some(([a, b]) => a === group.key || b === group.key);
        const anchor = groupAnchor(group.key);
        if (!overlapping || anchor === null) return;
        event.preventDefault();
        ui.openConflict({ date: current.date, id: group.key, anchor });
        return;
      }

      event.preventDefault();

      if (action.type === BlockKeyType.Clear) {
        ui.select(null);
        // Del never commits the field drafts first (2026-10b §5.4, path 5): the unsaved edits
        // go with the block. The picker and the overlap popover close with the block, or the
        // invisible overlay would keep swallowing the next press on the lane.
        if (ui.picker !== null) flushPicker(PickerFlushReason.Delete);
        ui.closePickerDiscarding();
        ui.closeConflict();
        void api.clearGroup(group);
        return;
      }

      if (action.type === BlockKeyType.MoveWhole) {
        // Alt+arrows move the start to the next step grid line in the direction (2026-10b
        // §3.5); Alt+PageUp/PageDown move by a whole hour without snapping, so an off-grid
        // block stays off the grid while a grid-aligned one stays aligned.
        const byHour = action.step === MoveStep.Hour;
        const delta = byHour
          ? action.direction * HOUR_MIN
          : nextGridLine(group.startMin, options.step, action.direction) - group.startMin;
        const members: IdSpan[] = group.members.map((member) => ({
          id: member.id,
          startMin: member.data.startMin,
          endMin: member.data.endMin,
        }));
        const changes = moveGroup(members, delta, options, !byHour);
        if (changes.length > 0) void api.resizeGroup(changes, []);
        return;
      }

      if (action.type === BlockKeyType.Overlay) {
        const span = { startMin: group.startMin, endMin: group.endMin };
        // Captured at command start (§4.3): a late outcome never settles a state entered since.
        const startedFrom = ui.quickMark;
        const quickId = startedFrom.projectId;
        if (quickId !== null) {
          // The "once" is consumed only on a successful outcome (§4.3): a duplicate keeps it.
          void api.createBlock(current.date, span, quickId).then((outcome) => {
            useDayUi.getState().quickMarkSettle(outcome, startedFrom);
          });
          return;
        }
        const anchor = groupAnchor(group.key);
        if (anchor !== null) {
          ui.openPicker({ kind: PickerKind.Create, date: current.date, span, anchor });
        }
        return;
      }

      const member =
        action.edge === ResizeEdge.Start ? group.members[0] : group.members[group.members.length - 1];
      if (member === undefined) return;
      // The nudged edge moves to the next step grid line in the direction (2026-10b §3.5): an
      // off-grid edge (14:10) first lands on the grid (14:30), then steps whole steps.
      const edgeMin = action.edge === ResizeEdge.Start ? member.data.startMin : member.data.endMin;
      const span = resizeBlockNoFlip(
        member.data,
        action.edge,
        nextGridLine(edgeMin, options.step, action.direction),
        options,
      );
      if (span.startMin !== member.data.startMin || span.endMin !== member.data.endMin) {
        void api.updateBlockSpan(member.id, { startMin: span.startMin, endMin: span.endMin });
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}
